import { useCallback, useEffect, useRef, useState } from "react";
import { IPaginationResponse } from "../../store/types";
interface IUsePaginationHook<DataType> {
  loading: boolean;
  data: DataType[];
  error: any;
  setData: (value: DataType[]) => void;
  hasMore: boolean;
  getNext: () => Promise<void>;
  refetch: () => void;
  total: number;
}

type AsyncActionType<DataType> = (
  page: number
) => Promise<IPaginationResponse<DataType>>;

export function usePaginationHook<DataType>(
  action: AsyncActionType<DataType>,
  doNotFetchData?: boolean[] | boolean
): IUsePaginationHook<DataType> {
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<DataType[]>([]);
  const [page, setPage] = useState<number>(1);
  const [error, setError] = useState<any>();
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);
  const [shouldRefetch, setShouldRefetch] = useState<boolean>(false);

  const setParamsToDefault = useCallback(() => {
    setLoading(false);
    setData([]);
    setPage(1);
    setError(null);
    setHasMore(true);
    setTotal(0);
    setShouldRefetch(false);
  }, []);

  const isMounted: React.MutableRefObject<boolean> = useRef<boolean>(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    return () => {
      isMounted.current = false;
    };
  }, []);

  const actionWrapper = useCallback(async () => {
    try {
      setLoading(true);
      const dataFromServer: IPaginationResponse<DataType> = await action(page);
      setData([...data, ...dataFromServer.data]);
      setPage(page + 1);
      setTotal(dataFromServer.total);
      if (dataFromServer.last_page === page) {
        setHasMore(false);
      }
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [page, data, hasMore, error, action, isMounted.current]);

  const refetch = useCallback(() => {
    setData([]);
    setPage(1);
    setHasMore(true);
    setError(null);
    setShouldRefetch(true);
  }, []);

  useEffect(() => {
    if (shouldRefetch) {
      setShouldRefetch(false);
      actionWrapper();
    }
  }, [shouldRefetch, page, data, hasMore, error, action]);

  useEffect(() => {
    if (typeof doNotFetchData === "boolean" && doNotFetchData) {
      setParamsToDefault();
      return;
    }

    if (typeof doNotFetchData === "object" && doNotFetchData?.includes(true)) {
      setParamsToDefault();
      return;
    }

    actionWrapper();
  }, [doNotFetchData]);

  return {
    loading,
    data,
    error,
    hasMore,
    setData,
    getNext: actionWrapper,
    refetch,
    total,
  };
}
